/**
 * Tenant selection for the Ecosystem Manager SPA.
 *
 * The selected tenant (`main` / `demo`) is persisted client-side in
 * localStorage and sent to the backend as a tRPC input. The backend resolves it
 * to a concrete tenant scope using its server-held access keys, and enforces the
 * `@cartesian.io` gate for `demo` in its auth middleware — the client-side gate
 * here is only a UX affordance.
 */

export type TenantId = 'main' | 'demo';

/** Tenant every authenticated user is allowed to use, regardless of email. */
export const DEFAULT_TENANT_ID: TenantId = 'main';

type TenantConfig = {
  displayName: string;
};

export const TENANT_CONFIGS: Record<TenantId, TenantConfig> = {
  main: { displayName: 'Main' },
  demo: { displayName: 'Demo' },
};

export const TENANT_IDS = Object.keys(TENANT_CONFIGS) as TenantId[];

export const TENANT_STORAGE_KEY = 'ecosystem-tenant';

export function isValidTenantId(value: string): value is TenantId {
  return Object.hasOwn(TENANT_CONFIGS, value);
}

export function isCartesianEmail(email: string): boolean {
  // Mirror the backend gate exactly: normalize casing/whitespace before the
  // domain check so variants like " Staff@Cartesian.IO " stay eligible.
  return email.trim().toLowerCase().endsWith('@cartesian.io');
}

export function readStoredTenant(): TenantId {
  if (typeof window === 'undefined') return DEFAULT_TENANT_ID;
  try {
    const value = window.localStorage.getItem(TENANT_STORAGE_KEY);
    return value && isValidTenantId(value) ? value : DEFAULT_TENANT_ID;
  } catch {
    // Storage may be unavailable (e.g. SecurityError in private mode).
    return DEFAULT_TENANT_ID;
  }
}

export function storeTenant(tenant: TenantId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TENANT_STORAGE_KEY, tenant);
  } catch {
    // Storage writes may fail (e.g. QuotaExceededError); persisting the
    // tenant is best-effort, so swallow the error rather than crash the SPA.
  }
}
